import { Auth } from "@aws-amplify/auth";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";

/**
 * Builds the Query Object for Lucene-style searching
 * Returns the raw object for the POST body
 */
const buildLucenePayload = ({
  searchValue,
  filterQuery,
  start,
  size,
  sort,
  sortBy,
  isSearching,
  queryOptions,
  searchText
}) => {
  const finalQuery = [searchValue, filterQuery]
    .filter(Boolean)
    .map((q) => `(${q})`)
    .join(" AND ");

  const MAX_OR_CLAUSES = 990;
  const countOrClauses = (str = "") => (str.match(/\sOR\s/g) || []).length;
  const totalOrClauses = countOrClauses(finalQuery || "");

  if (totalOrClauses > MAX_OR_CLAUSES) {
    snackbarService.show({
      message: "Too many conditions, please select less options",
      type: "error",
      duration: 5000
    });
  }

  const fields = isSearching ? queryOptions : undefined;

  const mustNotClause = [
    {
      script: {
        script: {
          source: "doc['_id'].size() > 0 && doc['_id'].value != null && doc['_id'].value.startsWith(params.prefix)",
          params: { prefix: "4epf_" }
        }
      }
    }
  ];

  const query = isSearching
    ? {
        bool: {
          must: [
            {
              query_string: {
                query: `*${searchText}*`,
                analyze_wildcard: true,
                default_operator: "AND",
                fields
              }
            },
            ...(finalQuery ? [{ query_string: { query: finalQuery } }] : [])
          ],
          must_not: mustNotClause
        }
      }
    : {
        bool: {
          must: [{ query_string: { query: finalQuery || "*" } }],
          must_not: mustNotClause
        }
      };

  return {
    from: start,
    size,
    track_total_hits: true,
    query,
    ...(sortBy && {
      sort: [{ [sortBy]: { order: sort } }]
    })
  };
};

/**
 * Builds the Query Object for DSL-style searching
 */
const buildQueryDSLPayload = (searchValue, start, size, sort, sortBy, queryOptions) => {
  const parsedQuery = typeof searchValue === "string" ? JSON.parse(searchValue) : searchValue;

  const queryBody = {
    from: start,
    size,
    track_total_hits: true,
    query: parsedQuery
  };

  if (sortBy) {
    queryBody.sort = [{ [sortBy]: { order: sort } }];
  }

  if (queryOptions?.fields && Array.isArray(queryOptions.fields)) {
    queryBody._source = queryOptions.fields;
  }

  return queryBody;
};

const processSearchResults = (data) => {
  const hits = data.hits?.hits || [];
  const count = data.hits?.total?.value || 0;

  return {
    data: hits.map((item) => ({ ...item._source, id: item._id })),
    count
  };
};

const getEmptyResult = () => ({ data: [], count: 0 });

/**
 * Main Exported Search Function
 */
export const OpenSearchConfig = async ({
  start = 0,
  size = 10,
  searchValue,
  sort = "asc",
  sortBy,
  filterQuery,
  queryOptions = [],
  isSearching = false,
  searchText = "",
  queryParser = "lucene"
}) => {
  try {
    const session = await Auth.currentSession();
    const authorizationid = session.getIdToken().getJwtToken();

    // 1. Get the JSON Payload instead of an endpoint string
    const payload =
      queryParser === "lucene"
        ? buildLucenePayload({
            searchValue,
            filterQuery,
            start,
            size,
            sort,
            sortBy,
            isSearching,
            queryOptions,
            searchText
          })
        : buildQueryDSLPayload(searchValue, start, size, sort, sortBy, queryOptions);

    const baseUrl = process.env.REACT_APP_AWS_OPENSEARCH_ENDPOINT;

    // 2. Perform the POST request
    const response = await fetch(`${baseUrl}/_search`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${authorizationid}`
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      return getEmptyResult();
    }

    const data = await response.json();
    return processSearchResults(data);
  } catch (error) {
    console.error("API error:", error);
    return getEmptyResult();
  }
};
